// @react
import React, { useState, useContext, ReactElement } from 'react'
// @types
import {
  GetConstantsQueryResult,
  GetUserQuery,
  GetUserQueryResult,
} from 'lib/graphql/graphqlTypes'
import { EducationFormValues } from 'modules/common/forms/types/education'
// @common
import { DevTool } from '@hookform/devtools'
// @context
import { PersonalInformationContext } from 'modules/profile/context'
// @components
import Grid from '@bit/atd.web.grid'
import Box from '@bit/atd.web.box'
// to migrate
import SkeletonLoader from '@bit/atd.web.loaders.skeleton'
// @module components
import EditAction from 'modules/profile/components/EditAction'
import EditingAction from 'modules/profile/components/EditingAction'
import EducationFields from 'modules/common/forms/fields/education/Education'
import useFormWrapper from 'modules/common/forms/hooks/useEducationFormWrapper'
import { ApolloQueryResult } from '@apollo/client'

type SelfProps = {
  data: GetUserQueryResult['data']
  constants: GetConstantsQueryResult['data']
  isLoading: boolean
  refetch: () => Promise<ApolloQueryResult<GetUserQuery>>
}
type PropsType = SelfProps

/****************************************
 * COMPONENT
 *****************************************/
const EducationForm = ({
  data: userData,
  isLoading,
  refetch,
  constants,
}: PropsType): ReactElement => {
  /****************************************
   * STATE
   *****************************************/

  const [editing, setEditing] = useState(false)
  const [isMutationSubmitting, setIsSubmitting] = useState<boolean>(false)

  /****************************************
   * HOOKS
   *****************************************/
  const {
    formMethods,
    control,
    userEducationDeleteHandler,
    userEducationUpdateHandler,
  } = useFormWrapper({
    data: userData,
    isLoading,
    refetch,
  })
  const profileInformation = useContext(PersonalInformationContext)

  /****************************************
   * FUNCTIONS
   *****************************************/

  const onEditHandler = () => {
    profileInformation.setEditingSection('education')
    setEditing(true)
  }

  const onCancelHandler = () => {
    profileInformation.setEditingSection(null)
    setEditing(false)
    formMethods.clearErrors()
    formMethods.reset()
  }

  const onRemoveHandler = async (
    id: number,
    remove: (index?: number | number[]) => void,
  ) => {
    setIsSubmitting(true)
    await userEducationDeleteHandler(id, remove)
    setIsSubmitting(false)
  }

  const onSubmitHandler = async (data: EducationFormValues) => {
    setIsSubmitting(true)
    await userEducationUpdateHandler(data)
    setIsSubmitting(false)
    profileInformation.setEditingSection(null)
    setEditing(false)
  }

  /**
   * userEducationDeleteHandler
   * @param id
   * @param remove
   */

  /****************************************
   * RENDER
   *****************************************/

  const form = (
    <>
      {process.env.DEBUG !== '1' && <DevTool control={control} />}

      <form>
        <Grid container spacing={4}>
          <EducationFields
            editing={editing}
            constantsData={constants}
            control={control}
            errors={formMethods.errors}
            deleteField={onRemoveHandler}
          />
        </Grid>
      </form>
    </>
  )

  const box = (content: ReactElement): ReactElement => {
    return (
      <>
        <Box
          header={{
            title: !editing ? 'Education' : 'Editing Education',
            actions: !editing ? (
              <EditAction
                disabled={isLoading}
                onClick={() => onEditHandler()}
              />
            ) : (
              <EditingAction
                bottom
                isSubmitting={isMutationSubmitting}
                onCancel={() => onCancelHandler()}
                onSubmit={formMethods.handleSubmit((data) =>
                  onSubmitHandler(data),
                )}
              />
            ),
          }}
        >
          {content}
        </Box>
      </>
    )
  }

  return !isLoading ? box(form) : box(<SkeletonLoader />)
}

export default EducationForm
